<template>
  <div class="container">
    <BasicTitle :title="pageTitle" />
    <BasicSubtitle subtitle="Los campos marcados con (*) son obligatorios." />
    <div class="container-form">
      <BasicLabel label="RUT / CI *" />
      <BorderInput
        v-model="form.taxId"
        label="RUT / CI *"
        maxlength="12"
        @input="onTaxIdInput"
      />
      <FormError :show="Boolean(errors.taxId)" :message="errors.taxId" />

      <BasicLabel label="Nombre *" />
      <BorderInput v-model="form.name" label="Nombre *" />
      <FormError :show="Boolean(errors.name)" :message="errors.name" />

      <BasicLabel label="Correo electrónico *" />
      <BorderInput v-model="form.email" label="Correo electrónico *" type="email" />
      <FormError :show="Boolean(errors.email)" :message="errors.email" />

      <BasicLabel label="Teléfono" />
      <BorderInput
        v-model="form.phone"
        label="Teléfono"
        maxlength="20"
        type="tel"
        inputmode="numeric"
        pattern="[0-9]*"
        @input="onPhoneInput"
        @keydown="onPhoneKeydown"
      />

      <BasicLabel label="Dirección" />
      <BorderInput v-model="form.address" label="Dirección" />

      <BasicLabel label="Sitio web" />
      <BorderInput v-model="form.website" label="Sitio web" placeholder="https://" />
      <FormError :show="Boolean(errors.website)" :message="errors.website" />

      <BasicLabel label="Comentario" />
      <BorderInput v-model="form.comment" label="Comentario" type="textarea" />
    </div>
    <PrimaryButton :label="primaryButtonLabel" :click="onSave" />
  </div>
</template>

<script>
import { computed, reactive, onMounted, onBeforeUnmount, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import store from "../../store";
import BasicTitle from "../../widgets/title/BasicTitle";
import BasicSubtitle from "../../widgets/subtitle/BasicSubtitle";
import BasicLabel from "../../widgets/label/BasicLabel";
import BorderInput from "../../widgets/input/BorderInput";
import FormError from "../../widgets/error/FormError";
import PrimaryButton from "../../widgets/button/PrimaryButton";
import { openMode, actions } from "../../constants";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WEBSITE_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/i;
const PHONE_CONTROL_KEYS = new Set([
  "Backspace",
  "Tab",
  "Enter",
  "Escape",
  "Delete",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
]);

const createEmptyErrors = () => ({
  taxId: "",
  name: "",
  email: "",
  website: "",
});

export default {
  components: {
    BasicTitle,
    BasicSubtitle,
    BasicLabel,
    BorderInput,
    FormError,
    PrimaryButton,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (!value) return null;
      return typeof value === "object" ? value.id : value;
    });

    const isEditMode = computed(() => store.state.openMode === openMode.MODIFY);
    const pageTitle = computed(() => (isEditMode.value ? "Editar proveedor" : "Nuevo proveedor"));
    const primaryButtonLabel = computed(() => (isEditMode.value ? "Actualizar" : "Guardar"));

    const form = reactive({
      id: null,
      taxId: "",
      name: "",
      email: "",
      phone: "",
      address: "",
      website: "",
      comment: "",
    });

    const errors = reactive(createEmptyErrors());

    const selectedSupplierId = computed(
      () => store.state.supplier.selectedSuppliers?.[0]?.id || store.state.supplier.current?.id || null
    );

    const clearErrors = () => {
      Object.keys(errors).forEach((key) => {
        errors[key] = "";
      });
    };

    const resetForm = () => {
      form.id = null;
      form.taxId = "";
      form.name = "";
      form.email = "";
      form.phone = "";
      form.address = "";
      form.website = "";
      form.comment = "";
      clearErrors();
    };

    const populateForm = (supplier) => {
      if (!supplier) return;
      form.id = supplier.id ?? null;
      form.taxId = supplier.taxId || "";
      form.name = supplier.name || "";
      form.email = supplier.email || "";
      form.phone = supplier.phone || "";
      form.address = supplier.address || "";
      form.website = supplier.website || "";
      form.comment = supplier.comment || "";
      clearErrors();
    };

    const loadSupplier = async () => {
      if (!isEditMode.value || !selectedSupplierId.value) {
        resetForm();
        return;
      }
      store.commit("setLoading", true);
      const response = await store.dispatch(actions.supplierActions.findOne, selectedSupplierId.value);
      store.commit("setLoading", false);
      if (response?.ok) {
        populateForm(response.data);
      } else {
        router.push("/contabilidad/proveedor/listado");
        store.commit("setToast", {
          type: "error",
          message: response?.message || "No fue posible cargar el proveedor seleccionado.",
        });
      }
    };

    const onTaxIdInput = (event) => {
      form.taxId = event.target.value.replace(/[^0-9kK]/g, "");
    };

    const onPhoneInput = (event) => {
      form.phone = event.target.value.replace(/\D/g, "");
    };

    const onPhoneKeydown = (event) => {
      if (
        PHONE_CONTROL_KEYS.has(event.key) ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
      ) {
        return;
      }

      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
      }
    };

    const validate = () => {
      clearErrors();
      if (!form.taxId) {
        errors.taxId = "Ingrese el documento";
      }
      if (!form.name) {
        errors.name = "Ingrese el nombre";
      }
      if (!form.email || !EMAIL_REGEX.test(form.email)) {
        errors.email = "Ingrese un correo válido";
      }
      if (form.website && !WEBSITE_REGEX.test(form.website)) {
        errors.website = "Ingrese una URL válida";
      }
      return !Object.values(errors).some(Boolean);
    };

    const onSave = async () => {
      if (!validate()) return;
      if (!buildingId.value) {
        store.commit("setToast", {
          type: "error",
          message: "Seleccione un edificio para continuar.",
        });
        return;
      }

      const payload = {
        id: form.id,
        taxId: form.taxId.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone?.trim() || "",
        address: form.address?.trim() || "",
        website: form.website?.trim() || "",
        comment: form.comment?.trim() || "",
        buildingId: buildingId.value,
      };

      store.commit("setLoading", true);
      try {
        let response;
        if (isEditMode.value && form.id) {
          response = await store.dispatch(actions.supplierActions.update, payload);
        } else {
          response = await store.dispatch(actions.supplierActions.create, payload);
        }
        if (response?.ok) {
          store.commit("setToast", {
            type: "success",
            message: isEditMode.value
              ? "Proveedor actualizado correctamente."
              : "Proveedor creado correctamente.",
          });
          store.commit("setSelectedSuppliers", []);
          router.push("/contabilidad/proveedor/listado");
        } else {
          if (response?.status === 409) {
            errors.taxId = "Ya existe un proveedor con ese RUT";
          }
          store.commit("setToast", {
            type: response?.status === 409 ? "warn" : "error",
            message: response?.message || "No fue posible guardar el proveedor.",
          });
        }
      } catch (error) {
        console.error(error);
        store.commit("setToast", {
          type: "error",
          message: "Ocurrió un error al guardar el proveedor.",
        });
      } finally {
        store.commit("setLoading", false);
      }
    };

    const addBreadcrumb = () => {
      const exists = store.state.general.breadcrumbs.find((item) => item.to === route.path);
      if (!exists) {
        const label = route.name || pageTitle.value;
        store.commit("addBreadcrumb", { label, to: route.path });
      }
    };

    onMounted(async () => {
      addBreadcrumb();
      store.commit("setLoading", true);
      try {
        if (isEditMode.value) {
          await loadSupplier();
        } else {
          resetForm();
        }
      } finally {
        store.commit("setLoading", false);
      }
    });

    onBeforeUnmount(() => {
      store.commit("removeBreadcrumb");
    });

    watch(
      () => form.taxId,
      () => {
        if (form.taxId) errors.taxId = "";
      }
    );

    watch(
      () => form.name,
      () => {
        if (form.name) errors.name = "";
      }
    );

    watch(
      () => form.email,
      () => {
        if (form.email && EMAIL_REGEX.test(form.email)) {
          errors.email = "";
        }
      }
    );

    watch(
      () => form.website,
      () => {
        if (!form.website || WEBSITE_REGEX.test(form.website)) {
          errors.website = "";
        }
      }
    );

    watch(buildingId, (value, oldValue) => {
      if (value && value !== oldValue && !isEditMode.value) {
        resetForm();
      }
    });

    return {
      store,
      form,
      errors,
      pageTitle,
      primaryButtonLabel,
      onSave,
      onTaxIdInput,
      onPhoneInput,
      onPhoneKeydown,
    };
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.container-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem 0;
  width: 100%;
  max-width: 600px;
}

.container-form > * {
  width: 100%;
  margin-bottom: 1.5rem;
}

.container-form > *:last-child {
  margin-bottom: 0;
}
</style>
